

import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { Button, Classname, DateHelper, Paginator, Scheme, useSearchParams } from "@rivtn/noseur";
import { ApiResponseData, BaseService, IProgram, ITournamentEdition, OmneHostService, SanitizedResponse, TournamentStatus } from "../../../services";
import { BaseI18nManager, i18nManager } from "../../../i18n";
import { Utils } from "../../../utils";
import { CountryFilter, CustomDropdown } from "./Leaderboard";

export const TournamentCard = ({ t }: { t: ITournamentEdition; }) => {
    const labels = i18nManager.Labels;
    const upcoming = t.status === TournamentStatus.UPCOMING;
    const actionText = (upcoming ? labels.program.tournaments.register : labels.program.tournaments.visit);
    const tournamentRegistrationOpen = DateHelper.isPast(new Date(t.dates.registration_start_date ?? "")) && DateHelper.isFuture(new Date(t.dates.registration_end_date ?? ""));
    const disableActionButton = (t.status === TournamentStatus.UPCOMING && (!tournamentRegistrationOpen || ((t.meta.total_expected_participants > -1 && t.meta.total_registered_participants >= t.meta.total_expected_participants))));

    return (
        <ReactRouterDom.NavLink key={t.external_id} to={{ pathname: `/to/${t.meta.public_id ?? t.tournament_id}`, search: "edi=" + t.external_id }} target="_blank" className={styles["t-card"]}>
            <div className={styles["t-img-box"]}>
                <img className={styles["banner"]} src={t.banner_url} alt={t.name} />
                <img className={styles["blogo"]} src={t.logo_url} alt={t.name} />
                <div className={styles["badge"]} style={{ backgroundColor: t.status === TournamentStatus.ONGOING ? '#ff3e3e' : t.status === TournamentStatus.UPCOMING ? '#0070ff' : '#222' }} >
                    {t.status}
                </div>
            </div>
            <div className={styles["t-info"]} style={{ padding: '24px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '10px', fontWeight: '800', marginBottom: '6px' }}>
                    {Utils.fineDate(new Date(t.dates.tournament_start_date!))}
                </div>
                <h3 style={{ textTransform: 'uppercase', fontStyle: 'italic', fontWeight: 900, fontSize: '20px', margin: '0 0 20px 0' }}>
                    {t.name}
                </h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-color)', paddingTop: '20px', flexWrap: "wrap" }}>
                    <div>
                        <ReactRouterDom.NavLink key={t.external_id} to={{ pathname: `/to/${t.meta.public_id ?? t.tournament_id}`, search: "edi=" + t.external_id + (upcoming ? "&register" : "") }} target="_blank"
                            className={Classname.build(styles["btn-primary"])} style={{ width: 'fit-content' }}>{actionText}</ReactRouterDom.NavLink>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: '800' }}>SLOTS</div>
                        <div style={{ fontWeight: 900, fontSize: '18px' }}>{t.meta.total_registered_participants}/{t.meta.total_expected_participants}</div>
                    </div>
                </div>
            </div>
        </ReactRouterDom.NavLink>
    );
};

export interface TournamentsProps {
    program: IProgram;
}

function Tournaments(props: TournamentsProps) {

    const { program } = props;
    const labels = i18nManager.Labels;
    const omnehostService = OmneHostService.getInstance();
    const [tournamentEditions, setTournamentEditions] = React.useState<ApiResponseData<ITournamentEdition>>();
    const { page, setPage, statuses, setStatuses, country, setCountry } = useSearchParams({ defaultInit: window.location.search, params: { page: "1", statuses: "UPCOMING,ONGOING,ENDED", country: "" } });

    React.useEffect(fetchTournaments, [omnehostService, page, statuses, country]);

    return (<div className={styles["container"]} style={{ paddingTop: '100px' }} ref={(r) => r?.scrollIntoView()}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <h1 style={{ fontSize: '48px', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>
                {labels.program.tournaments.tournaments_hub}
            </h1>
            <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', marginBottom: '40px', alignItems: 'flex-end' }}>
                <div>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px' }}>Category</div>
                    <div style={{ display: 'flex', gap: '8px', background: 'var(--bg-surface)', padding: '6px', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
                        <button
                            onClick={() => updateStatusFilter("UPCOMING")}
                            className={`${styles["nav-btn"]} ${statuses.includes("UPCOMING") ? styles["active"] : ''}`}
                            style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '10px' }}
                        >
                            Upcoming
                        </button>
                        <button
                            onClick={() => updateStatusFilter("ONGOING")}
                            className={`${styles["nav-btn"]} ${statuses.includes("ONGOING") ? styles["active"] : ''}`}
                            style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '10px' }}
                        >
                            Ongoing
                        </button>
                        <button
                            onClick={() => updateStatusFilter("ENDED")}
                            className={`${styles["nav-btn"]} ${statuses.includes("ENDED") ? styles["active"] : ''}`}
                            style={{ padding: '8px 24px', borderRadius: '10px', fontSize: '10px' }}
                        >
                            Ended
                        </button>
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px' }}>Country</div>
                    <CustomDropdown selected={country} options={[["", "All"]].concat(BaseI18nManager._countryCurrency.map((e) => ([e.country_code, e.country_name])))} onSelect={setCountry} />
                </div>
            </div>
        </div>
        {(!!tournamentEditions?.content)
            ? ((tournamentEditions.content && tournamentEditions.content?.length > 0)
                ? (<div className={styles["tournament-grid"]}>
                    {tournamentEditions.content!.map((t) => <TournamentCard key={t.external_id} t={t} />)}
                </div>)
                : (Utils.emptyState()))
            : (Utils.loadingState())}
        <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={tournamentEditions?.size} totalRecords={tournamentEditions?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
            setPage("" + e.currentPage)
        }} template={{
            activePageLabel: () => "",
            nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
        }} />
    </div>);

    function updateStatusFilter(status: string) {
        let newStatuses = statuses.split(",");
        const index = newStatuses.indexOf(status);
        if (index > -1) {
            newStatuses.splice(index, 1);
        } else {
            newStatuses.push(status);
        }
        setStatuses(newStatuses.join(","));
    }

    function fetchTournaments() {
        setTournamentEditions({});
        omnehostService.queryTournamentEditions({
            page,
            sort: "status,DESC",
            country_code: country,
            program_id: program.external_id,
            status__in: statuses.split(","),
        }).then(async ({ apiResponse }: SanitizedResponse<ITournamentEdition>) => {
            setTournamentEditions(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Tournaments;
